import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { cn } from "@/app/handlers/cn.utils";

const PAGE_WIDTH = 816; // 8.5in at 96dpi
const PAGE_HEIGHT = 1056; // 11in at 96dpi

/**
 * A scaled-down, non-interactive first-page preview of a rendered template.
 * The template document renders at real page width inside an isolated iframe,
 * then is scaled to the requested thumbnail width.
 */
export function ResumeThumb({ html, width = 224, className }: { html: string; width?: number; className?: string }) {
	const scale = width / PAGE_WIDTH;
	return (
		<div
			className={cn("overflow-hidden bg-white", className)}
			style={{ width, height: Math.round(PAGE_HEIGHT * scale) }}
		>
			<iframe
				srcDoc={html}
				title="Template preview"
				scrolling="no"
				tabIndex={-1}
				aria-hidden="true"
				style={{
					width: PAGE_WIDTH,
					height: PAGE_HEIGHT,
					border: 0,
					transform: `scale(${scale})`,
					transformOrigin: "top left",
					pointerEvents: "none",
				}}
			/>
		</div>
	);
}

export interface ResumeViewerHandle {
	print: () => void;
}

/**
 * A full, selectable preview of the whole resume, scaled to fit its container
 * width. Exposes `print()` so the caller can trigger the browser print dialog
 * (used for Download PDF) against the template's own @media print styles.
 */
export const ResumeViewer = forwardRef<ResumeViewerHandle, { html: string; className?: string }>(
	({ html, className }, ref) => {
		const wrapRef = useRef<HTMLDivElement>(null);
		const iframeRef = useRef<HTMLIFrameElement>(null);
		const [scale, setScale] = useState(1);
		const [docHeight, setDocHeight] = useState(PAGE_HEIGHT);

		useImperativeHandle(ref, () => ({ print: () => iframeRef.current?.contentWindow?.print() }), []);

		useEffect(() => {
			const el = wrapRef.current;
			if (!el) return;
			const observer = new ResizeObserver(() => setScale(Math.min(1, el.clientWidth / PAGE_WIDTH)));
			observer.observe(el);
			return () => observer.disconnect();
		}, []);

		const measure = () => {
			const doc = iframeRef.current?.contentDocument;
			if (doc) setDocHeight(doc.documentElement.scrollHeight);
		};

		return (
			<div ref={wrapRef} className={className} style={{ height: Math.round(docHeight * scale) }}>
				<iframe
					ref={iframeRef}
					srcDoc={html}
					title="Tailored resume"
					onLoad={measure}
					scrolling="no"
					style={{
						width: PAGE_WIDTH,
						height: docHeight,
						border: 0,
						transform: `scale(${scale})`,
						transformOrigin: "top left",
					}}
				/>
			</div>
		);
	},
);

ResumeViewer.displayName = "ResumeViewer";

/**
 * Print a self-contained resume document. Opens it in a new window and prints
 * that window, so only the resume (with its own @media print styles) is printed.
 * iOS Safari cannot reliably print an iframe's contentWindow — it prints the host
 * page instead — so a fresh window is used rather than the on-screen preview iframe.
 */
export function printResumeDocument(html: string) {
	const win = window.open("", "_blank");
	if (!win) return;
	win.document.open();
	win.document.write(html);
	win.document.close();
	win.focus();
	const print = () => win.print();
	if (win.document.readyState === "complete") setTimeout(print, 350);
	else win.onload = () => setTimeout(print, 350);
}
